import { useState, useEffect, useContext } from 'react';
import { Table, Button, Input, Form, Modal, Select, Spin, Popconfirm, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';
import { LoginContext } from '../../../context/loginContext';

const { Option } = Select;

const CCentroCostos = () => {
    const [centrosCostos, setCentrosCostos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingCentroCosto, setEditingCentroCosto] = useState(null);
    const [form] = Form.useForm();
    const { user, selectedEmpresa } = useContext(LoginContext);

    useEffect(() => {
        fetchCentrosCostos();
    }, []);

    const fetchCentrosCostos = async () => {
        setLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/centro-costo/${selectedEmpresa.emp_id}`;
            const config = {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            };
            const response = await axios.get(url, config);
            setCentrosCostos(response.data);
        } catch (error) {
            console.error('Error obteniendo los centros de costos:', error);
            message.error('Error al cargar los centros de costos');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditingCentroCosto(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingCentroCosto(record);
        form.setFieldsValue({
            cen_codigo: record.cen_codigo,
            cen_nombre: record.cen_nombre,
        });
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/centro-costo/${id}`;
            const config = {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            };
            await axios.delete(url, config);
            message.success('Centro de costo eliminado correctamente');
            fetchCentrosCostos();
        } catch (error) {
            console.error('Error eliminando el centro de costo:', error);
            message.error('Error al eliminar el centro de costo');
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const data = {
                ...values,
                cen_empresa: selectedEmpresa?.emp_id, // Se usa selectedEmpresa en lugar de user
                cen_baja: false
            };

            if (editingCentroCosto) {
                // Actualizar
                const url = `${process.env.REACT_APP_API_URL}/centro-costo/${editingCentroCosto.cen_id}`;
                const config = {
                    headers: {
                        Authorization: `Bearer ${user.token}`,
                    },
                };
                await axios.put(url, data, config);
                message.success('Centro de costo actualizado correctamente');
            } else {
                // Crear nuevo
                const url = `${process.env.REACT_APP_API_URL}/centro-costo`;
                const config = {
                    headers: {
                        Authorization: `Bearer ${user.token}`,
                    },
                };
                await axios.post(url, data, config);
                message.success('Centro de costo creado correctamente');
            }

            setModalVisible(false);
            fetchCentrosCostos();
        } catch (error) {
            console.error('Error guardando el centro de costo:', error);
            message.error('Error al guardar el centro de costo');
        }
    };

    const columns = [
        {
            title: 'Nombre',
            dataIndex: 'cen_nombre',
            key: 'cen_nombre',
        },
        {
            title: 'Acciones',
            key: 'action',
            render: (_, record) => (
                <>
                    <Button 
                        type="primary" 
                        icon={<EditOutlined />} 
                        onClick={() => handleEdit(record)}
                        style={{ marginRight: 8 }}
                    />
                    <Popconfirm
                        title="¿Está seguro de eliminar este centro de costo?"
                        onConfirm={() => handleDelete(record.cen_id)}
                        okText="Sí"
                        cancelText="No"
                    >
                        <Button danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </>
            ),
        },
    ];

    return (
        <div>
            <h1>Centros de Costos</h1>
            
            <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={handleAdd}
                style={{ marginBottom: 16 }}
            >
                Agregar Centro de Costo
            </Button>

            {loading ? (
                <div style={{ textAlign: 'center', margin: '20px 0' }}>
                    <Spin size="large" />
                </div>
            ) : (
                <Table 
                    dataSource={centrosCostos} 
                    columns={columns} 
                    rowKey="cen_id"
                    pagination={false}
                />
            )}

            {/* Modal para crear/editar centros de costo */}
            <Modal
                title={editingCentroCosto ? "Editar Centro de Costo" : "Nuevo Centro de Costo"}
                visible={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
                okText={editingCentroCosto ? "Actualizar" : "Crear"}
                cancelText="Cancelar"
            >
                <Form
                    form={form}
                    layout="vertical"
                >
                    <Form.Item
                        name="cen_nombre"
                        label="Nombre"
                        rules={[{ required: true, message: 'Por favor ingrese el nombre' }]}
                    >
                        <Input placeholder="Nombre del centro de costo" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default CCentroCostos;